import React, { useEffect, useState } from "react";
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from "@mui/material";
import GetData from "../../services/GetData.js";
import PostData from "../../services/PostData.js";
import { SingleSelectField } from "../Products/TypedFields.jsx";
import {
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
} from "../Products/catalogGridStyles.js";

const PLATFORMS = ["eBay", "Walmart", "Shopify", "Facebook Marketplace"];
const STATUSES = ["Pending Label", "Fulfilled", "Cancelled"];

function dayInputValue(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function catalogOptionLabel(item) {
    if (!item) return "";
    const parts = [item.itemId || ""];
    if (item.macSubId != null && item.macSubId !== "") {
        parts.push(`MAC ${item.macSubId}`);
    }
    if (item.status) parts.push(item.status);
    const productName = item.product?.itemName || item.automatedInvoiceItemName;
    if (productName) parts.push(productName);
    return parts.filter(Boolean).join(" · ");
}

const emptyForm = () => ({
    platform: "eBay",
    catalogItem: null,
    price: "",
    orderDate: dayInputValue(),
    status: "Pending Label",
    buyerName: "",
});

export default function ManualAddOrderDialog({ open, onClose, onCreated }) {
    const [form, setForm] = useState(emptyForm);
    const [catalogQuery, setCatalogQuery] = useState("");
    const [catalogOptions, setCatalogOptions] = useState([]);
    const [searchingCatalog, setSearchingCatalog] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return;
        setForm(emptyForm());
        setCatalogQuery("");
        setCatalogOptions([]);
        setError("");
        setSaving(false);
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const q = catalogQuery.trim();
        if (q.length < 2) {
            setCatalogOptions([]);
            return undefined;
        }
        let cancelled = false;
        const timer = setTimeout(async () => {
            setSearchingCatalog(true);
            const { getData } = await GetData(
                `broylesllc/catalog-items?q=${encodeURIComponent(q)}&status=Listed&pageSize=20`
            );
            if (!cancelled) {
                setCatalogOptions(Array.isArray(getData?.items) ? getData.items : []);
                setSearchingCatalog(false);
            }
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [catalogQuery, open]);

    const setField = (key) => (event) => {
        setForm((prev) => ({ ...prev, [key]: event.target.value }));
    };
    const setSelect = (key) => (value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const canSave =
        form.platform &&
        form.catalogItem?._id &&
        form.buyerName.trim() &&
        form.orderDate &&
        form.status &&
        form.price !== "" &&
        Number.isFinite(Number(form.price)) &&
        Number(form.price) >= 0;

    const handleSave = async () => {
        if (!canSave || saving) return;
        setSaving(true);
        setError("");
        const response = await PostData(
            {
                sellingChannel: form.platform,
                catalogItemId: form.catalogItem._id,
                price: Number(form.price),
                orderDate: form.orderDate,
                orderStatus: form.status,
                buyerName: form.buyerName.trim(),
            },
            "broylesllc/orders",
            null
        );
        setSaving(false);
        if (!response?.success) {
            setError(
                response?.error?.response?.data?.message ||
                    "Could not create the order."
            );
            return;
        }
        onCreated?.(response.data);
        onClose?.();
    };

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            scroll="paper"
            PaperProps={{ sx: { borderRadius: "14px" } }}
        >
            <DialogTitle sx={{ fontWeight: 700 }}>Manual Add Order</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
                    <SingleSelectField
                        label="Platform"
                        value={form.platform}
                        onChange={setSelect("platform")}
                        choices={PLATFORMS}
                    />
                    <Autocomplete
                        size="small"
                        options={catalogOptions}
                        loading={searchingCatalog}
                        value={form.catalogItem}
                        inputValue={catalogQuery}
                        onInputChange={(_event, value, reason) => {
                            if (reason === "reset") return;
                            setCatalogQuery(value);
                        }}
                        onChange={(_event, item) => {
                            setForm((prev) => ({ ...prev, catalogItem: item }));
                            setCatalogQuery(item ? catalogOptionLabel(item) : catalogQuery);
                        }}
                        getOptionLabel={(item) => catalogOptionLabel(item)}
                        isOptionEqualToValue={(option, value) =>
                            String(option?._id) === String(value?._id)
                        }
                        filterOptions={(options) => options}
                        noOptionsText={
                            catalogQuery.trim().length < 2
                                ? "Type at least 2 characters"
                                : searchingCatalog
                                  ? "Searching…"
                                  : "No matching Listed catalog items"
                        }
                        renderOption={(props, option) => (
                            <li {...props} key={option._id}>
                                <span>{catalogOptionLabel(option)}</span>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Catalog item"
                                placeholder="Search by item ID"
                                required
                            />
                        )}
                    />
                    <TextField
                        label="Price"
                        size="small"
                        type="number"
                        value={form.price}
                        onChange={setField("price")}
                        fullWidth
                        required
                        inputProps={{ min: 0, step: "0.01" }}
                    />
                    <TextField
                        label="Date"
                        size="small"
                        type="date"
                        value={form.orderDate}
                        onChange={setField("orderDate")}
                        fullWidth
                        required
                        InputLabelProps={{ shrink: true }}
                    />
                    <SingleSelectField
                        label="Status"
                        value={form.status}
                        onChange={setSelect("status")}
                        choices={STATUSES}
                    />
                    <TextField
                        label="Buyer name"
                        size="small"
                        value={form.buyerName}
                        onChange={setField("buyerName")}
                        fullWidth
                        required
                    />
                    {error ? (
                        <Box sx={{ color: "#b91c1c", fontSize: 13 }}>{error}</Box>
                    ) : null}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={saving} sx={catalogQuietButtonSx}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={!canSave || saving}
                    onClick={handleSave}
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
                    sx={catalogPrimaryButtonSx}
                >
                    Save order
                </Button>
            </DialogActions>
        </Dialog>
    );
}
